const METRIKA_SCRIPT_ID = 'yandex-metrika-tag';
const METRIKA_SCRIPT_SRC = 'https://mc.yandex.ru/metrika/tag.js';

let initializedCounterId = null;
let buttonTrackingCleanup = null;

const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

const normalizeCounterId = (value) => {
  const parsed = Number.parseInt(String(value || '').trim(), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

export const getMetrikaCounterId = () => normalizeCounterId(process.env.REACT_APP_YM_ID);

const ensureYmStub = () => {
  if (typeof window.ym === 'function') {
    return;
  }

  window.ym = function () {
    window.ym.a = window.ym.a || [];
    window.ym.a.push(arguments);
  };
  window.ym.l = Date.now();
};

const ensureMetrikaScript = () => {
  if (document.getElementById(METRIKA_SCRIPT_ID)) {
    return;
  }

  const script = document.createElement('script');
  script.id = METRIKA_SCRIPT_ID;
  script.async = true;
  script.src = METRIKA_SCRIPT_SRC;
  const firstScript = document.getElementsByTagName('script')[0];

  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
    return;
  }

  document.head.appendChild(script);
};

const getCurrentPath = () => {
  if (!isBrowser) {
    return '/';
  }

  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
};

const getActiveCounterId = () => initializedCounterId || getMetrikaCounterId();

const canTrack = () => {
  if (!isBrowser) {
    return false;
  }

  if (typeof window.ym !== 'function') {
    return false;
  }

  return Boolean(getActiveCounterId());
};

const compactText = (value = '') => value.replace(/\s+/g, ' ').trim();

const getButtonLabel = (buttonNode) => {
  const labelFromDataset = compactText(buttonNode.dataset.metrikaLabel || '');
  if (labelFromDataset) {
    return labelFromDataset;
  }

  const ariaLabel = compactText(buttonNode.getAttribute('aria-label') || '');
  if (ariaLabel) {
    return ariaLabel;
  }

  const textContent = compactText(buttonNode.textContent || '');
  if (textContent) {
    return textContent.slice(0, 140);
  }

  return 'button';
};

const getButtonSection = (buttonNode) => {
  const sectionFromDataset = compactText(buttonNode.dataset.metrikaSection || '');
  if (sectionFromDataset) {
    return sectionFromDataset;
  }

  const section = buttonNode.closest('section[id], [data-metrika-section]');
  if (!section) {
    return 'unknown';
  }

  return compactText(section.getAttribute('data-metrika-section') || section.id || 'unknown') || 'unknown';
};

export const initMetrika = (counterId = getMetrikaCounterId()) => {
  if (!isBrowser) {
    return false;
  }

  const normalizedId = normalizeCounterId(counterId);
  if (!normalizedId) {
    return false;
  }

  if (initializedCounterId === normalizedId) {
    return true;
  }

  ensureYmStub();
  ensureMetrikaScript();

  window.ym(normalizedId, 'init', {
    defer: true,
    clickmap: true,
    trackLinks: true,
    accurateTrackBounce: true,
    webvisor: true,
  });

  initializedCounterId = normalizedId;
  return true;
};

export const trackPageView = (url = getCurrentPath()) => {
  if (!canTrack()) {
    return;
  }

  window.ym(getActiveCounterId(), 'hit', url);
};

export const trackGoal = (goalId, params = {}) => {
  if (!goalId || !canTrack()) {
    return;
  }

  const hasParams = params && Object.keys(params).length > 0;

  if (hasParams) {
    window.ym(getActiveCounterId(), 'reachGoal', goalId, params);
    return;
  }

  window.ym(getActiveCounterId(), 'reachGoal', goalId);
};

export const attachGlobalButtonTracking = () => {
  if (!isBrowser || buttonTrackingCleanup) {
    return buttonTrackingCleanup;
  }

  const onClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('button, [role="button"]') : null;
    if (!target) {
      return;
    }

    if (target.dataset.metrikaIgnore === 'true') {
      return;
    }

    trackGoal('button_click', {
      label: getButtonLabel(target),
      section: getButtonSection(target),
      path: getCurrentPath(),
    });
  };

  document.addEventListener('click', onClick, true);

  buttonTrackingCleanup = () => {
    document.removeEventListener('click', onClick, true);
    buttonTrackingCleanup = null;
  };

  return buttonTrackingCleanup;
};
